// Камера рельефной карты v8: перспектива с наклоном, который зависит от масштаба (отдалили — почти сверху,
// приблизили — смотрим под углом с юга). Одни и те же матрицы у WebGL-земли и у 2D-слоя объектов: точка карты
// (x, y клетки, z высота) → пиксель экрана и обратно (нажатие → клетка на земле).
//
// Оси GL: X = x карты, Y = −y карты (север вверху), Z — высота (в клетках).
import type { TileView } from './viewport';

export interface CameraLike {
  x: number;
  y: number;
  zoom: number;
}

export interface View3D {
  w: number;
  h: number;
  /** Пикселей на клетку в центре экрана (как BASE_TILE·zoom у плоской карты). */
  ts: number;
  /** Наклон от вертикали, радианы. */
  pitch: number;
  fovY: number;
  dist: number;
  eye: [number, number, number];
  target: [number, number, number];
  /** Матрица вида·проекции (column-major, как ждёт WebGL). */
  viewProj: Float32Array;
  view: Float32Array;
  proj: Float32Array;
  inv: Float32Array;
  /** Камера: вправо и вверх экрана в осях GL (для спрайтов-«billboard»). */
  right: [number, number, number];
  up: [number, number, number];
}

/** Вертикальный угол обзора. */
export const FOV_Y = (32 * Math.PI) / 180;
/** Высота точки, вокруг которой вращается камера (средний уровень суши). */
export const TARGET_Z = 0.7;
export const PITCH_MIN = (5 * Math.PI) / 180;
export const PITCH_MAX = (50 * Math.PI) / 180;

/** Масштабы карты v8 относительно обычного: отдалить до 0.33, приблизить до 1.7. */
export const ZOOM_MIN_SHARE = 0.33;
export const ZOOM_MAX_SHARE = 1.7;

function smooth(a: number, b: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

/** Наклон камеры по масштабу (base — обычный масштаб). */
export function pitchForZoom(zoom: number, base: number): number {
  const t = smooth(base * ZOOM_MIN_SHARE, base * ZOOM_MAX_SHARE, zoom);
  return PITCH_MIN + (PITCH_MAX - PITCH_MIN) * t;
}

function perspective(fovy: number, aspect: number, near: number, far: number): Float32Array {
  const f = 1 / Math.tan(fovy / 2);
  const nf = 1 / (near - far);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = (far + near) * nf;
  m[11] = -1;
  m[14] = 2 * far * near * nf;
  return m;
}

function lookAt(eye: number[], c: number[], up: number[]): { m: Float32Array; s: number[]; u: number[] } {
  let fx = c[0]! - eye[0]!;
  let fy = c[1]! - eye[1]!;
  let fz = c[2]! - eye[2]!;
  const fl = Math.hypot(fx, fy, fz);
  fx /= fl;
  fy /= fl;
  fz /= fl;
  let sx = fy * up[2]! - fz * up[1]!;
  let sy = fz * up[0]! - fx * up[2]!;
  let sz = fx * up[1]! - fy * up[0]!;
  const sl = Math.hypot(sx, sy, sz);
  sx /= sl;
  sy /= sl;
  sz /= sl;
  const ux = sy * fz - sz * fy;
  const uy = sz * fx - sx * fz;
  const uz = sx * fy - sy * fx;
  const m = new Float32Array(16);
  m[0] = sx;
  m[4] = sy;
  m[8] = sz;
  m[1] = ux;
  m[5] = uy;
  m[9] = uz;
  m[2] = -fx;
  m[6] = -fy;
  m[10] = -fz;
  m[12] = -(sx * eye[0]! + sy * eye[1]! + sz * eye[2]!);
  m[13] = -(ux * eye[0]! + uy * eye[1]! + uz * eye[2]!);
  m[14] = fx * eye[0]! + fy * eye[1]! + fz * eye[2]!;
  m[15] = 1;
  return { m, s: [sx, sy, sz], u: [ux, uy, uz] };
}

export function mul4(a: Float32Array, b: Float32Array): Float32Array {
  const o = new Float32Array(16);
  for (let c = 0; c < 4; c++)
    for (let r = 0; r < 4; r++) {
      let s = 0;
      for (let k = 0; k < 4; k++) s += a[k * 4 + r]! * b[c * 4 + k]!;
      o[c * 4 + r] = s;
    }
  return o;
}

export function invert4(m: Float32Array): Float32Array {
  const a = m;
  const o = new Float32Array(16);
  const b00 = a[0]! * a[5]! - a[1]! * a[4]!;
  const b01 = a[0]! * a[6]! - a[2]! * a[4]!;
  const b02 = a[0]! * a[7]! - a[3]! * a[4]!;
  const b03 = a[1]! * a[6]! - a[2]! * a[5]!;
  const b04 = a[1]! * a[7]! - a[3]! * a[5]!;
  const b05 = a[2]! * a[7]! - a[3]! * a[6]!;
  const b06 = a[8]! * a[13]! - a[9]! * a[12]!;
  const b07 = a[8]! * a[14]! - a[10]! * a[12]!;
  const b08 = a[8]! * a[15]! - a[11]! * a[12]!;
  const b09 = a[9]! * a[14]! - a[10]! * a[13]!;
  const b10 = a[9]! * a[15]! - a[11]! * a[13]!;
  const b11 = a[10]! * a[15]! - a[11]! * a[14]!;
  let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
  if (!det) return o;
  det = 1 / det;
  o[0] = (a[5]! * b11 - a[6]! * b10 + a[7]! * b09) * det;
  o[1] = (a[2]! * b10 - a[1]! * b11 - a[3]! * b09) * det;
  o[2] = (a[13]! * b05 - a[14]! * b04 + a[15]! * b03) * det;
  o[3] = (a[10]! * b04 - a[9]! * b05 - a[11]! * b03) * det;
  o[4] = (a[6]! * b08 - a[4]! * b11 - a[7]! * b07) * det;
  o[5] = (a[0]! * b11 - a[2]! * b08 + a[3]! * b07) * det;
  o[6] = (a[14]! * b02 - a[12]! * b05 - a[15]! * b01) * det;
  o[7] = (a[8]! * b05 - a[10]! * b02 + a[11]! * b01) * det;
  o[8] = (a[4]! * b10 - a[5]! * b08 + a[7]! * b06) * det;
  o[9] = (a[1]! * b08 - a[0]! * b10 - a[3]! * b06) * det;
  o[10] = (a[12]! * b04 - a[13]! * b02 + a[15]! * b00) * det;
  o[11] = (a[9]! * b02 - a[8]! * b04 - a[11]! * b00) * det;
  o[12] = (a[5]! * b07 - a[4]! * b09 - a[6]! * b06) * det;
  o[13] = (a[0]! * b09 - a[1]! * b07 + a[2]! * b06) * det;
  o[14] = (a[13]! * b01 - a[12]! * b03 - a[14]! * b00) * det;
  o[15] = (a[8]! * b03 - a[9]! * b01 + a[10]! * b00) * det;
  return o;
}

/** Камера для экрана w×h (CSS px): цель — центр экрана в клетках, ts — пикселей на клетку в центре. */
export function makeView(cam: CameraLike, w: number, h: number, ts: number, pitch: number): View3D {
  const fovY = FOV_Y;
  const dist = h / (2 * ts * Math.tan(fovY / 2));
  const target: [number, number, number] = [cam.x, -cam.y, TARGET_Z];
  const eye: [number, number, number] = [
    cam.x,
    -cam.y - dist * Math.sin(pitch),
    TARGET_Z + dist * Math.cos(pitch),
  ];
  // «Вверх» камеры — на север (при взгляде сверху вниз экрана — юг)
  const la = lookAt(eye, target, [0, Math.cos(pitch), Math.sin(pitch)]);
  const proj = perspective(fovY, w / h, Math.max(0.5, dist * 0.08), dist * 8 + 200);
  const viewProj = mul4(proj, la.m);
  return {
    w,
    h,
    ts,
    pitch,
    fovY,
    dist,
    eye,
    target,
    viewProj,
    view: la.m,
    proj,
    inv: invert4(viewProj),
    right: la.s as [number, number, number],
    up: la.u as [number, number, number],
  };
}

export interface ScreenPoint {
  x: number;
  y: number;
  /** Пикселей на клетку в этой точке (перспектива: ближе — крупнее). */
  s: number;
  /** Глубина (для сортировки и отсечения). */
  w: number;
}

/** Точка карты (x, y — клетки, z — высота) → экран (CSS px). */
export function project(v: View3D, x: number, y: number, z = 0): ScreenPoint {
  const m = v.viewProj;
  const X = x;
  const Y = -y;
  const cx = m[0]! * X + m[4]! * Y + m[8]! * z + m[12]!;
  const cy = m[1]! * X + m[5]! * Y + m[9]! * z + m[13]!;
  const cw = m[3]! * X + m[7]! * Y + m[11]! * z + m[15]!;
  const iw = 1 / Math.max(1e-4, cw);
  return {
    x: (cx * iw * 0.5 + 0.5) * v.w,
    y: (1 - (cy * iw * 0.5 + 0.5)) * v.h,
    s: v.h / (2 * Math.tan(v.fovY / 2) * Math.max(1e-4, cw)),
    w: cw,
  };
}

function rayAt(v: View3D, sx: number, sy: number): { o: number[]; d: number[] } {
  const nx = (sx / v.w) * 2 - 1;
  const ny = 1 - (sy / v.h) * 2;
  const m = v.inv;
  const tr = (z: number) => {
    const x = m[0]! * nx + m[4]! * ny + m[8]! * z + m[12]!;
    const y = m[1]! * nx + m[5]! * ny + m[9]! * z + m[13]!;
    const zz = m[2]! * nx + m[6]! * ny + m[10]! * z + m[14]!;
    const w = m[3]! * nx + m[7]! * ny + m[11]! * z + m[15]!;
    return [x / w, y / w, zz / w];
  };
  const a = tr(-1);
  const b = tr(1);
  return { o: a, d: [b[0]! - a[0]!, b[1]! - a[1]!, b[2]! - a[2]!] };
}

/**
 * Экран → точка карты (клетки, дробные) на земле. heightAt — высота рельефа; несколько итераций
 * пересечения луча с поверхностью (для холмов и гор).
 */
export function unproject(
  v: View3D,
  sx: number,
  sy: number,
  heightAt?: (x: number, y: number) => number,
): { x: number; y: number } {
  const { o, d } = rayAt(v, sx, sy);
  let z = TARGET_Z;
  let x = 0;
  let y = 0;
  for (let k = 0; k < (heightAt ? 4 : 1); k++) {
    const t = Math.abs(d[2]!) < 1e-6 ? 0 : (z - o[2]!) / d[2]!;
    x = o[0]! + d[0]! * t;
    y = -(o[1]! + d[1]! * t);
    if (heightAt) z = 0.5 * z + 0.5 * heightAt(x, y);
  }
  return { x, y };
}

/** Область карты (клетки), видимая на экране, с запасом pad клеток. */
export function viewTiles(v: View3D, pad = 6): TileView {
  const pts = [unproject(v, 0, 0), unproject(v, v.w, 0), unproject(v, 0, v.h), unproject(v, v.w, v.h)];
  // Верх экрана при наклоне захватывает дальнюю землю; горы выше — небольшой запас вверх
  return {
    x0: Math.min(...pts.map((p) => p.x)) - pad,
    y0: Math.min(...pts.map((p) => p.y)) - pad - 4,
    x1: Math.max(...pts.map((p) => p.x)) + pad,
    y1: Math.max(...pts.map((p) => p.y)) + pad,
  };
}
